// The debrief narrative: the one place in the app where a language model writes prose for the player.
// It reports conclusions the simulation already reached — the decision log, the lost-time list, the
// statistics, the order log — and nothing else, with per-paragraph citations validated against the very
// event ids the prompt listed (docs/design/ai-patterns.md §5 "AI as rapporteur"). The raw logs sit
// beside it on this page, never behind it, and the template fallback runs if the model is slow or absent.

import { useEffect, useMemo, useRef, useState } from 'react';
import * as audit from '../../ai/audit';
import { callLLM } from '../../ai/client';
import { buildDebriefPrompt, debriefTimeoutMs } from '../../ai/debriefPrompt';
import { DebriefSchema } from '../../ai/schemas';
import type { DebriefContext } from '../../ai/templates';
import { templateDebrief } from '../../ai/templates';
import { validateCitations } from '../../ai/validate';
import type { Grid } from '../../sim/grid';
import type { Event, Order, Unit } from '../../sim/types';
import { useDebrief } from '../../state/debrief';
import { clockHM } from '../reports';
import { cardLabel, eventLine } from './format';
import { Heading } from './Logs';

type Paragraph = { heading: string; text: string; cited_event_ids: number[] };

interface NarrativeState {
  paragraphs: Paragraph[];
  source: 'llm' | 'cached' | 'template' | 'flagged';
  error?: string;
}

export function Narrative({
  ctx, outcome, durationMin, decisions, lostTimeFull, events, units, grid, decisionPointsSummary,
}: {
  ctx: DebriefContext;
  outcome: string;
  durationMin: number;
  decisions: { event: Event; blueOrders: Order[]; redDecisions: Event[] }[];
  lostTimeFull: { item: string; minutes: number; eventIds: number[] }[];
  events: Event[];
  units: Unit[];
  grid: Grid | null;
  /**
   * The decision points as prose (`decisionPointsSummary` in src/debrief/analyze.ts), appended to the
   * facts the rapporteur is given.
   *
   * It is appended here rather than added to `DebriefContext` because `src/ai/` is another agent's
   * territory this week; the summary cites only event ids that are already in the decision log, so
   * `knownEventIds` needs nothing added and citation validation is unaffected. Fold it into
   * `buildDebriefPrompt`'s own facts object when the two branches meet.
   */
  decisionPointsSummary?: string;
}) {
  const [state, setState] = useState<NarrativeState | null>(null);
  /**
   * React's StrictMode mounts, unmounts and remounts in development. The narrative is one request per
   * run, so the request is guarded by this ref rather than cancelled on cleanup: cancelling would throw
   * away the only in-flight request and the second mount would refuse to start another.
   */
  const requested = useRef(false);
  const eventById = useMemo(() => new Map(events.map((e) => [e.id, e])), [events]);
  const scrubToEvent = useDebrief((s) => s.scrubToEvent);

  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    const prompt = buildDebriefPrompt(ctx, {
      outcome,
      durationMin,
      decisions,
      lostTimeFull,
      eventLine: (ev) => eventLine(ev, units, grid),
      cardLabel,
    });

    const user = decisionPointsSummary
      ? `${prompt.user}\n\nDECISION POINTS (the Decision Support Matrix the debrief built from this run; the event id of each is in the line):\n${decisionPointsSummary}`
      : prompt.user;

    void callLLM(
      {
        model: 'sonnet',
        system: prompt.system,
        user,
        schema: DebriefSchema,
        timeoutMs: debriefTimeoutMs(),
        // Six paragraphs with citations, and a reasoning model that spends part of its budget
        // thinking first: at the proxy's 2048 default the answer came back cut off mid-object and
        // every run fell through to the template ("could not parse JSON from response text").
        maxTokens: 6000,
        purpose: 'debrief',
        auditRef: 'after-action narrative',
      },
      () => templateDebrief(ctx),
    ).then((res) => {
      // Grounding: a paragraph may only cite event ids the prompt actually listed, and the whole
      // narrative is flagged if the model cited more ids that do not exist than ids that do.
      let flagged = false;
      let citedTotal = 0;
      let citedDropped = 0;
      const paragraphs = res.value.paragraphs.map((p) => {
        citedTotal += p.cited_event_ids.length;
        const checked = validateCitations(p, prompt.knownEventIds);
        citedDropped += checked.dropped;
        if (checked.flagged) flagged = true;
        return checked.value;
      });
      // Six paragraphs are validated one at a time, so the whole narrative's grounding result is
      // summed onto its one audit row here (src/ai/audit.ts).
      audit.note(res.auditId, { citedTotal, citedDropped, flagged });
      setState({
        paragraphs,
        source: res.source === 'llm' ? (flagged ? 'flagged' : res.cached ? 'cached' : 'llm') : 'template',
        error: res.error,
      });
    });

    // The payload is frozen history: build the narrative once per run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section data-testid="narrative" className="wt-panel p-3">
      <div className="flex items-baseline justify-between gap-2">
        <Heading>After-action narrative</Heading>
        <SourceChip state={state} />
      </div>
      <p className="mt-1 text-[11px] leading-snug text-fog">
        Written by the rapporteur from the logs above and nothing else. Every paragraph cites the events
        it rests on; click a citation to scrub the replay to that minute.
      </p>

      {!state && (
        <p className="mono mt-3 text-[12px] text-fog" data-testid="narrative-loading">
          writing the after-action review…
        </p>
      )}

      <div className="mt-3 space-y-3">
        {state?.paragraphs.map((p, i) => (
          <article key={`${p.heading}-${i}`} data-testid="narrative-paragraph">
            <h3 className="mono text-[11px] uppercase tracking-[0.18em]" style={{ color: 'var(--color-friendly)' }}>
              {p.heading}
            </h3>
            <p className="mt-1 text-[13px] leading-relaxed text-paper">{p.text}</p>
            {p.cited_event_ids.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {p.cited_event_ids.map((id) => {
                  const ev = eventById.get(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      data-testid="narrative-citation"
                      onClick={() => ev && scrubToEvent(ev.t)}
                      title={ev ? eventLine(ev, units, grid) : 'Event not in the log'}
                      className="mono rounded-sm border border-slate-700 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.12em] text-fog hover:text-[color:var(--color-friendly)]"
                    >
                      #{id}{ev ? ` ${clockHM(ev.t)}` : ''}
                    </button>
                  );
                })}
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

const SOURCE_NOTE: Record<NarrativeState['source'], string> = {
  llm: 'Written by the model from the listed facts.',
  cached: 'The model wrote this on a recorded run of the same facts; it was replayed from the demo cache rather than called again.',
  template: 'The model was slow or unreachable; this is the deterministic template summary.',
  flagged: 'The model cited events that do not exist — read this one against the logs.',
};

function SourceChip({ state }: { state: NarrativeState | null }) {
  if (!state) return null;
  const tone = state.source === 'llm' ? 'var(--color-isr)'
    : state.source === 'cached' ? 'var(--color-friendly)'
    : state.source === 'template' ? 'var(--color-fog)'
    : 'var(--color-warn)';
  return (
    <span
      data-testid="narrative-source"
      data-source={state.source}
      title={`${SOURCE_NOTE[state.source]}${state.error ? ` (${state.error})` : ''}`}
      className="mono rounded-sm border px-1.5 py-0.5 text-[10px] uppercase tracking-[0.12em]"
      style={{ borderColor: tone, color: tone }}
    >
      {state.source}
    </span>
  );
}
